/**
 * RenderToggles — in-world debug overlay toggle buttons for the Race Debug
 * panel. Each toggle flips a render flag in RaceDebugOverlayState that the
 * RaceDebugRenderer (game-loop system) reads each frame.
 *
 * Toggles:
 *   📦 Hitboxes  — AABB outlines around hazards (red)
 *   ⭐ Powerups  — AABB outlines around powerup pickups (orange)
 *   🏁 Checkpoints — checkpoint / start-gate / finish-line zones (cyan)
 */

import React, { useState } from 'react';
import {
  isRenderHitboxesEnabled,
  isRenderPowerupBoxesEnabled,
  isRenderCheckpointsEnabled,
  toggleRenderHitboxes,
  toggleRenderPowerupBoxes,
  toggleRenderCheckpoints,
} from '../../../systems/race/RaceDebugOverlayState';
import { panelBtnStyle } from './RaceDebugPrimitives';

const toggleStyle: React.CSSProperties = {
  ...panelBtnStyle,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '3px 8px',
  fontSize: '10px',
  width: '100%',
  justifyContent: 'space-between',
};

const activeStyle: React.CSSProperties = {
  ...toggleStyle,
  background: 'rgba(46, 204, 113, 0.15)',
  border: '1px solid #2ecc71',
  color: '#fff',
};

const dotStyle = (color: string): React.CSSProperties => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: color,
  flexShrink: 0,
});

export const RenderToggles: React.FC = () => {
  const [, forceUpdate] = useState(0);
  const hitboxes = isRenderHitboxesEnabled();
  const powerups = isRenderPowerupBoxesEnabled();
  const checkpoints = isRenderCheckpointsEnabled();

  const bump = () => forceUpdate((n) => n + 1);

  return (
    <div style={{ marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      <div style={{ fontSize: '10px', color: '#888', fontWeight: 'bold', marginBottom: '3px' }}>
        In-World Overlays
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <button
          style={hitboxes ? activeStyle : toggleStyle}
          onClick={() => { toggleRenderHitboxes(); bump(); }}
          title="Toggle hazard AABB outlines (red)"
        >
          <span>📦 Hazards</span>
          {hitboxes && <span style={dotStyle('#ff4444')} />}
        </button>
        <button
          style={powerups ? activeStyle : toggleStyle}
          onClick={() => { toggleRenderPowerupBoxes(); bump(); }}
          title="Toggle powerup AABB outlines (orange)"
        >
          <span>⭐ Powerups</span>
          {powerups && <span style={dotStyle('#f39c12')} />}
        </button>
        <button
          style={checkpoints ? activeStyle : toggleStyle}
          onClick={() => { toggleRenderCheckpoints(); bump(); }}
          title="Toggle checkpoint / start-gate / finish-line zone outlines (cyan)"
        >
          <span>🏁 Checkpoints</span>
          {checkpoints && <span style={dotStyle('#00ffff')} />}
        </button>
      </div>
    </div>
  );
};
